import { formatPepLoginError } from 'features/PMSReports/pepLoginCopy'
import { useCallback, useEffect, useState } from 'react'

const OPEN_PEP_LOGIN_MODAL = 'OPEN_PEP_LOGIN_MODAL'
const CLOSE_PEP_LOGIN_MODAL = 'CLOSE_PEP_LOGIN_MODAL'
const PEP_LOGIN_MODAL_RESULT = 'PEP_LOGIN_MODAL_RESULT'

function parseMessagePayload(event) {
  if (!event?.message) {
    return null
  }
  try {
    return typeof event.message === 'string' ? JSON.parse(event.message) : event.message
  } catch {
    return null
  }
}

export function usePepLoginModal() {
  const [modalState, setModalState] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeModal = useCallback(() => {
    setModalState(null)
    setSubmitError(null)
    setIsSubmitting(false)
  }, [])

  const applyOpenPayload = useCallback(payload => {
    if (!payload?.flowId) {
      return
    }
    setModalState(payload)
    setSubmitError(null)
    setIsSubmitting(false)
    if (window.electron?.send) {
      window.electron.send('pep-login-modal-ack', { flowId: payload.flowId })
    }
  }, [])

  const handleMainMessage = useCallback((_, event) => {
    if (!event?.type) {
      return
    }

    if (event.type === OPEN_PEP_LOGIN_MODAL) {
      applyOpenPayload(parseMessagePayload(event))
      return
    }

    if (event.type === CLOSE_PEP_LOGIN_MODAL) {
      const payload = parseMessagePayload(event)
      if (!payload?.flowId || payload.flowId === modalState?.flowId) {
        closeModal()
      }
      return
    }

    if (event.type === PEP_LOGIN_MODAL_RESULT) {
      const payload = parseMessagePayload(event)
      if (!payload?.flowId || payload.flowId !== modalState?.flowId) {
        return
      }
      setIsSubmitting(false)
      if (payload.success) {
        closeModal()
        return
      }
      setSubmitError(formatPepLoginError(payload.step, payload.error))
    }
  }, [closeModal, modalState?.flowId, applyOpenPayload])

  useEffect(() => {
    if (!window.electron?.onMessage) {
      return undefined
    }
    window.electron.onMessage(handleMainMessage)
    return () => {
      if (typeof window.electron.offMessage === 'function') {
        window.electron.offMessage(handleMainMessage)
      }
    }
  }, [handleMainMessage])

  useEffect(() => {
    if (!window.electron?.on) {
      return undefined
    }
    window.electron.on('open-pep-login-modal', applyOpenPayload)
  }, [applyOpenPayload])

  const submitCredentials = useCallback(
    async credentials => {
      if (!modalState?.flowId || !window.electron?.invoke) {
        return
      }
      setIsSubmitting(true)
      setSubmitError(null)
      try {
        const result = await window.electron.invoke('pep-login-submit', credentials)
        if (result?.success) {
          closeModal()
          return
        }
        setSubmitError(formatPepLoginError(result?.step, result?.error))
      } catch {
        setSubmitError(formatPepLoginError('unknown step'))
      } finally {
        setIsSubmitting(false)
      }
    },
    [closeModal, modalState?.flowId]
  )

  const cancelModal = useCallback(() => {
    if (modalState?.flowId && window.electron?.send) {
      window.electron.send('pep-login-cancel', { flowId: modalState.flowId })
    }
    closeModal()
  }, [closeModal, modalState?.flowId])

  const snoozeModal = useCallback(
    minutes => {
      if (modalState?.flowId && window.electron?.send) {
        window.electron.send('pep-login-snooze', {
          flowId: modalState.flowId,
          minutes
        })
      }
      closeModal()
    },
    [closeModal, modalState?.flowId]
  )

  return {
    modalState,
    submitError,
    isSubmitting,
    submitCredentials,
    cancelModal,
    snoozeModal,
    isOpen: modalState != null
  }
}

export default usePepLoginModal
